/**
 * DevLicensePage · v2.6.9 batch BT
 * ==================================
 * Inline (non-modal) Developer License page that replaces the
 * `DevLicenseDialog` popup. Linked from the Admin Console sidebar's
 * "Dev License" entry — clicking now navigates here instead of opening
 * a dialog.
 *
 * Content is identical to the prior dialog: Bison AI branding, live
 * /system-status indicators (polled every 15s), license metadata,
 * developer statement and copyright. Layout uses a two-column grid
 * on desktop (status left, license metadata right) so it makes use of
 * the full page real estate.
 *
 * v2.6.9 batch BT · logo asset updated to /bison-ai-logo.png (new asset).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ScrollText, ShieldCheck, ArrowLeft } from "lucide-react";
import api from "@/lib/api";

function StatusDot({ ok, color = "emerald" }) {
  if (ok === null) return <span className="inline-flex h-2.5 w-2.5 rounded-full bg-zinc-600" data-testid="status-dot-loading"/>;
  if (ok) {
    const cls = color === "blue" ? "bg-blue-400" : "bg-emerald-400";
    return (
      <span className="relative inline-flex h-2.5 w-2.5" data-testid={`status-dot-on-${color}`}>
        <span className={`absolute inline-flex h-full w-full rounded-full opacity-70 animate-ping ${cls}`}/>
        <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${cls}`}/>
      </span>
    );
  }
  return <span className="inline-flex h-2.5 w-2.5 rounded-full bg-red-500" data-testid="status-dot-off"/>;
}

function StatusRow({ label, ok, sub, color = "emerald" }) {
  const statusText = ok === null ? "checking…" : ok ? "active" : "offline";
  const statusColor = ok === null ? "text-zinc-400" : ok ? (color === "blue" ? "text-blue-400" : "text-emerald-400") : "text-red-400";
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-zinc-900 last:border-0">
      <div className="min-w-0">
        <div className="text-sm text-zinc-200 font-semibold truncate">{label}</div>
        {sub && <div className="text-[11px] text-zinc-500 truncate">{sub}</div>}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <StatusDot ok={ok} color={color}/>
        <span className={`text-[10px] uppercase tracking-wider font-bold ${statusColor}`}>{statusText}</span>
      </div>
    </div>
  );
}

function InfoRow({ label, value, valueClass }) {
  return (
    <div className="grid grid-cols-[120px_1fr] gap-2 py-1 text-sm border-b border-zinc-900 last:border-0">
      <div className="text-zinc-500 uppercase tracking-wider text-[10px] self-center">{label}</div>
      <div className={`text-zinc-200 ${valueClass || ""}`}>{value}</div>
    </div>
  );
}

export default function DevLicensePage() {
  const [status, setStatus] = useState({ online: null, dc: null, ai: null, gps: null, video: null, dcLocation: "North America / Oklahoma City · net-1" });

  useEffect(() => {
    let alive = true;
    const refresh = () => {
      api.get("/system-status").then((r) => {
        if (!alive) return;
        setStatus({
          online: !!r.data.online,
          dc: !!r.data.data_center?.active,
          ai: !!r.data.bison_ai?.active,
          gps: !!r.data.bison_ai_gps?.active,
          video: !!r.data.bison_ai_video_audio?.active,
          dcLocation: r.data.data_center?.location || "North America / Oklahoma City · net-1",
        });
      }).catch(() => {
        if (!alive) return;
        setStatus({ online: false, dc: false, ai: false, gps: false, video: false, dcLocation: "North America / Oklahoma City · net-1" });
      });
    };
    refresh();
    const t = setInterval(refresh, 15_000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const year = new Date().getFullYear();

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="dev-license-page">
      {/* Header */}
      <header className="border-b border-zinc-900 bg-gradient-to-b from-zinc-900 to-zinc-950">
        <div className="max-w-5xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link to="/admin" data-testid="dev-license-back">
            <Button variant="outline" size="sm" className="bg-zinc-950 border-zinc-800 text-zinc-300 hover:bg-zinc-900">
              <ArrowLeft className="h-4 w-4 mr-1.5"/>Back to Admin
            </Button>
          </Link>
          <div className="flex flex-col items-center text-center">
            <img
              src="/bison-ai-logo.png"
              alt="Bison AI"
              className="h-16 select-none pointer-events-none"
              style={{ filter: "drop-shadow(0 1px 6px rgba(0,0,0,0.5))" }}
              data-testid="dev-license-logo"
            />
            <div className="h-[3px] w-48 -mt-[2px] mt-2 rounded-full" style={{ background: "linear-gradient(to right, transparent 0%, #18181b 35%, #18181b 65%, transparent 100%)" }}/>
            <div className="text-xs uppercase tracking-[0.3em] text-amber-400 font-bold pt-2" data-testid="dev-license-title">Developer License</div>
          </div>
          <div className="w-[110px]"/>{/* spacer to keep title centered */}
        </div>
      </header>

      {/* Two-column body */}
      <main className="max-w-5xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* LEFT · live status */}
          <Card className="bg-zinc-900/70 border-zinc-800 p-5" data-testid="dev-license-status">
            <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300 font-bold mb-3">Live System Status</div>
            <StatusRow label="Online Status" ok={status.online}/>
            <StatusRow label="Data Center Status" sub={status.dcLocation} ok={status.dc}/>
            <StatusRow label="Bison AI — Artificial Intelligence" ok={status.ai}/>
            <StatusRow label="BISON AI GPS" ok={status.gps}/>
            <StatusRow label="Bison AI Video & Audio Monitoring" ok={status.video} color="blue"/>
          </Card>

          {/* RIGHT · build / developer */}
          <Card className="bg-zinc-900/70 border-zinc-800 p-5" data-testid="dev-license-dev">
            <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300 font-bold mb-3">Developer · Build</div>
            <InfoRow label="OS" value={<span><b className="text-amber-300">BISON V2</b> · License #121980</span>}/>
            <InfoRow label="Build" value="Automatic to releases"/>
            <InfoRow label="Developer" value="Derick Mashaney"/>
            <InfoRow label="Owner" value="BISON AI & Technology, LLC."/>
            <InfoRow label="Phone" value="(405) 506-8920"/>
            <InfoRow label="Email" value={<a href="mailto:support@bison-software.com" className="hover:text-amber-300">support@bison-software.com</a>}/>
            <InfoRow label="Website" value={<a href="https://www.bison-software.com" target="_blank" rel="noopener noreferrer" className="hover:text-amber-300">www.bison-software.com</a>}/>
          </Card>
        </div>

        {/* Licensed-To */}
        <Card className="mt-6 bg-amber-400/5 border-amber-400/30 p-5" data-testid="dev-license-licensee">
          <div className="text-[10px] uppercase tracking-[0.25em] text-amber-300 font-bold mb-3">Licensed To</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
            <InfoRow label="Company" value="Buller Taxi Services"/>
            <InfoRow label="Status" value={<span className="text-emerald-400 font-bold uppercase tracking-wider text-[11px]" data-testid="license-status">Current</span>}/>
            <InfoRow label="Serial #" value={
              <span className="flex items-center gap-2" data-testid="license-serial-row">
                <span className="font-mono text-[11px] text-zinc-200">BAI-2442-9871-5482-13</span>
                <StatusDot ok={status.online}/>
                <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-400">{status.online === null ? "checking…" : status.online ? "Active" : "Offline"}</span>
              </span>
            }/>
            <InfoRow label="Website" value={<a href="https://www.bullertaxi.com" target="_blank" rel="noopener noreferrer" className="hover:text-amber-300">www.bullertaxi.com</a>}/>
            <InfoRow label="Email" value={<a href="mailto:support@bullertaxi.com" className="hover:text-amber-300">support@bullertaxi.com</a>}/>
            <InfoRow label="Phone" value="(580) 219-4687"/>
          </div>
        </Card>

        {/* Developer statement */}
        <div className="mt-6 border-t border-zinc-900 pt-6" data-testid="dev-license-statement">
          <div className="flex items-start gap-2 mb-2">
            <ScrollText className="h-3.5 w-3.5 text-amber-400 flex-shrink-0 mt-0.5"/>
            <div className="text-[10px] uppercase tracking-[0.25em] text-amber-300 font-bold">Developer Statement</div>
          </div>
          <p className="text-sm text-zinc-300 leading-relaxed italic max-w-3xl">
            All software, content, branding, logos, artwork and source code are property of BISON AI &amp; Technology<sup>®™</sup> and Derick Mashaney. This source code may not be copied or used without the developer&apos;s explicit permission.
          </p>
          <div className="mt-4 pt-4 border-t border-zinc-900 flex items-center justify-between gap-2">
            <p className="text-xs text-zinc-500 leading-tight">
              &copy; {year} <span className="text-zinc-300 font-bold">BISON AI &amp; Technology<sup>®™</sup></span>. <em className="font-bold not-italic-no text-zinc-200" style={{ fontStyle: "italic" }}>Taxi Ride-Share Software Company</em> — All rights reserved.
            </p>
            <ShieldCheck className="h-5 w-5 text-amber-400/60 flex-shrink-0"/>
          </div>
        </div>
      </main>
    </div>
  );
}
